// Terminal-app instance of @eidollon/auth-client wired to the Zustand store.
// All HTTP/refresh/401 logic lives in the package; this file is just glue.
//
// Inference-aware layer:
//   The API returns 503 { error: "inference_unavailable" } when
//   services/inference is unreachable. We translate that into a typed
//   InferenceUnavailableError so AI surfaces can hide cleanly without the
//   error surfacing as a generic console error. The flag also flips a
//   Zustand bit (useInferenceStatus.offline) so the Header can show an
//   unobtrusive "AI offline" pill.

import { ApiError, createAuthClient } from '@eidollon/auth-client';
import { readAccessToken, useAuth } from '../state/auth.js';
import { useInferenceStatus } from '../state/inference-status.js';
import { API_BASE_URL, AUTH_BASE_URL } from './env.js';

export class InferenceUnavailableError extends Error {
  readonly code = 'inference_unavailable' as const;
  readonly status = 503 as const;
  constructor(public readonly requestId?: string) {
    super('inference_unavailable');
    this.name = 'InferenceUnavailableError';
  }
}

export function isInferenceUnavailableError(e: unknown): e is InferenceUnavailableError {
  return e instanceof InferenceUnavailableError;
}

const client = createAuthClient({
  authBaseUrl: AUTH_BASE_URL,
  apiBaseUrl: API_BASE_URL,
  getAccessToken: readAccessToken,
  onTokenIssued: (token, me) => useAuth.getState().setAccess(token, me),
  onSignOut: () => useAuth.getState().signOutLocal(),
});

export const authFetch = client.authFetch;
export const bootProbe = client.bootProbe;
export { ApiError };

// Wrap apiFetch so a 503 inference_unavailable becomes a typed error and
// flips the offline flag. Anything else propagates unchanged.
export const apiFetch = async <T = unknown>(
  path: string,
  opts?: Parameters<typeof client.apiFetch>[1],
): Promise<T> => {
  try {
    const out = await client.apiFetch<T>(path, opts);
    // A previously offline gateway is now responding to any /v1/* call —
    // reachable enough that we can drop the offline flag and let surfaces
    // refresh on their next render.
    if (useInferenceStatus.getState().offline) {
      useInferenceStatus.getState().setOffline(false);
    }
    return out;
  } catch (e) {
    if (e instanceof ApiError && e.status === 503 && e.code === 'inference_unavailable') {
      useInferenceStatus.getState().setOffline(true);
      throw new InferenceUnavailableError(e.requestId);
    }
    throw e;
  }
};
